import { useEffect, useRef } from "react";
import { installShortcutListeners } from "@/utils/keyboardShortcuts";

/**
 * React hook: installs a global keyboard shortcut listener with the given
 * handlers map. Re-installs when the handlers identity changes (use
 * useCallback for stable handlers).
 *
 * Example:
 *   useKeyboardShortcuts({
 *     startEvent: () => startNext(),
 *     revealJ1: () => reveal(1),
 *   });
 */
export default function useKeyboardShortcuts(handlers) {
  // Keep a stable ref to the latest handlers so we don't re-install on every
  // re-render. The listener reads from the ref each keypress.
  const ref = useRef(handlers);
  useEffect(() => {
    ref.current = handlers;
  }, [handlers]);

  useEffect(() => {
    const proxy = {};
    for (const k of Object.keys(handlers || {})) {
      proxy[k] = (...args) => {
        const fn = ref.current && ref.current[k];
        if (typeof fn === "function") fn(...args);
      };
    }
    return installShortcutListeners(proxy);
  }, [Object.keys(handlers || {}).sort().join("|")]);
}
